import {
  ReplaceTranscriptSuffixFailedError,
  TeamPolicyRejectedError,
  TranscriptChangedError,
  getStores,
  replaceTranscriptSuffix,
  type LocalTranscriptSegment,
} from '@littlebird/sdk';
import { toJS, when } from 'mobx';

import { backendApis } from '@/backendApis';
import { FEATURES, hasFeature } from '@/features/feature-flags';
import { meetingDebugStore } from '@/features/meeting-debug/meeting-debug.store';
import { logger } from '@/lib/logger';
import { resolvePreferredTranscriptionEngine } from '@/lib/meetings/transcription-engine';
import { userStore } from '@/stores/user.store';
import { AppCapability, isCapable } from '@/utils';

import { electronApis } from '../electron-api';

import { setMeetingPostProcessingActive } from './meeting-activity';
import { splitTranscriptSegments } from './meetings-transcript.utils';
import { ppDebug } from './meetings.debug-log';
import {
  type MeetingPostProcessingJobState,
  type MeetingPostProcessingParkedState,
  type RunningPostProcessingLedgerFields,
} from './meetings.durable-map';
import {
  beginTranscriptProcessingRound,
  completeTranscriptProcessingRound,
  releaseTranscriptProcessingRound,
  type FinalizedPostProcessingRound,
} from './meetings.processing-lifecycle';
import { meetingStore } from './meetings.state';

import type { RecordingSession } from './meetings.recording-session';
import type {
  MeetingPostProcessingStateChangedPayload,
  TranscriptionEngine,
} from '@electron/shared/meetings.types';

/** How a round ended, for the finalization breadcrumb (design §8.3). */
export type MeetingFinalizationKind =
  | 'replaced'
  | 'kept_live'
  | 'parked'
  | 'discarded_stale'
  // Native reported the round retryable (no transcript to replace): we
  // summarized the intact live transcript but kept the audio un-acked so the
  // round still reprocesses at the next stop/recovery.
  | 'failed_retryable_kept_live';

const MAX_BACKEND_UPLOAD_FINALIZATION_ATTEMPTS = 3;
const POST_PROCESSING_FINALIZATION_DRAIN_TIMEOUT_MS = 5000;
const POST_PROCESSING_SYNC_UNKNOWN_GRACE_MS = 15_000;
/** How long to wait for an in-flight summary workflow to leave `in_progress`
 * before summarizing again (design §3.3: a summarize while one runs no-ops). */
const SUMMARY_SETTLE_TIMEOUT_MS = 60_000;

let onPostProcessingFinalized:
  | ((meetingId: string, summaryGenerated: boolean) => void)
  | undefined;

/** Registered by the query layer so a successful post-processing finalization
 * restores the summary-ready polling/notification that live-path stops get at
 * stop time (for these meetings the summary only exists after finalization). */
export function setOnPostProcessingFinalized(
  callback: (meetingId: string, summaryGenerated: boolean) => void
) {
  onPostProcessingFinalized = callback;
}

const postProcessingUploadsInFlight = new Map<string, Promise<void>>();
/** userId that owned each meeting's in-flight finalization when it was
 * requested; entries live only while the finalization chain runs. */
const finalizationOwnerUserIds = new Map<string, string | undefined>();
const postProcessingStateHandlers = new Map<
  string,
  (state: MeetingPostProcessingStateChangedPayload) => Promise<void> | void
>();
let unlistenPostProcessingState: (() => void) | undefined;

function syncPostProcessingActivity() {
  setMeetingPostProcessingActive(
    postProcessingStateHandlers.size > 0 ||
      postProcessingUploadsInFlight.size > 0
  );
}

type TerminalStatePayload = Extract<
  MeetingPostProcessingStateChangedPayload,
  { state: 'completed' | 'failed' }
>;

type RoundLedger = {
  replaceFromOffset: number | null;
  sessionEpoch?: number;
  processingToken?: string;
  expectedLength?: number;
  deferSummary: boolean;
};

type ParkedRoundRecord = Extract<
  MeetingPostProcessingJobState,
  { state: 'failed' }
>;

/** A stored `failed` record is always a parked round, carrying the completed
 * segments and the round ledger. */
function isParkedRound(
  record: MeetingPostProcessingJobState | undefined
): record is ParkedRoundRecord {
  return record?.state === 'failed';
}

/** Reads the round ledger from a running or parked record. */
function ledgerFromRecord(
  record: MeetingPostProcessingJobState | undefined
): RoundLedger {
  if (!record) return { replaceFromOffset: null, deferSummary: false };
  return {
    replaceFromOffset: record.replaceFromOffset,
    sessionEpoch: record.sessionEpoch,
    processingToken: record.processingToken,
    expectedLength: record.expectedLength,
    deferSummary: record.deferSummary ?? false,
  };
}

export function shouldUseLocalPostProcessing(
  activeTranscriptionEngine?: TranscriptionEngine
) {
  // Deepgram diarizes and names speakers live; the local engine does neither, so it keeps the post pass
  if (
    hasFeature(FEATURES.REALTIME_SPEAKER_ATTRIBUTION) &&
    activeTranscriptionEngine === 'remote'
  ) {
    return false;
  }
  if (
    activeTranscriptionEngine === 'local' &&
    hasFeature(FEATURES.LOCAL_TRANSCRIPTION_POST_PROCESSING)
  ) {
    return true;
  }
  if (hasFeature(FEATURES.DEEPGRAM_TRANSCRIPTION_DIARIZATION)) {
    return true;
  }
  return (
    activeTranscriptionEngine === 'remote' &&
    resolvePreferredTranscriptionEngine() === 'local' &&
    hasFeature(FEATURES.LOCAL_TRANSCRIPTION_DIARIZATION) &&
    hasFeature(FEATURES.LOCAL_TRANSCRIPTION_POST_PROCESSING)
  );
}

/**
 * Runs `finalize` for the meeting, serialized per meeting. If a finalization
 * is already in flight (round k), a new request (e.g. round k+1's terminal
 * arriving mid-flight) is queued to run after the in-flight one settles
 * instead of being dropped, so no terminal is ever swallowed. Every queued
 * run must re-validate against the then-current ledger record when it starts:
 * terminal handling re-runs its staleness check, and the parked-round paths
 * re-check the captured record via `isLedgerRecordCurrent`.
 */
function runPostProcessingFinalization(
  meetingId: string,
  finalize: () => Promise<void>
) {
  // The user signed in when this finalization was requested owns its ledger
  // writes; `canWriteLedger` compares against this capture.
  const owningUserId = userStore.userId;
  const previousFinalization = postProcessingUploadsInFlight.get(meetingId);

  const finalization = (async () => {
    // A failed predecessor was already observed by its own caller and must
    // not poison this run.
    await previousFinalization?.catch(() => {});
    finalizationOwnerUserIds.set(meetingId, owningUserId);
    await finalize();
  })();
  postProcessingUploadsInFlight.set(meetingId, finalization);
  syncPostProcessingActivity();
  // Only the tail of the chain owns (and may remove) the map entries. Errors
  // still propagate to this run's caller through `finalization` itself.
  void finalization
    .catch(() => {})
    .finally(() => {
      if (postProcessingUploadsInFlight.get(meetingId) === finalization) {
        postProcessingUploadsInFlight.delete(meetingId);
        finalizationOwnerUserIds.delete(meetingId);
        syncPostProcessingActivity();
      }
    });
  return finalization;
}

/** An in-flight finalization can survive a token-expiry logout or an account
 * switch. Its ledger writes must then stop: they would stamp the old user's
 * round with the new user's id, or re-insert an entry after the logout clear.
 * True when no finalization is running for the meeting, or the signed-in user
 * still matches the finalization's owner. */
function canWriteLedger(meetingId: string): boolean {
  if (!finalizationOwnerUserIds.has(meetingId)) return true;
  if (finalizationOwnerUserIds.get(meetingId) === userStore.userId) return true;
  ppDebug('ledger_write_skipped_user_switch', { meetingId });
  return false;
}

/** True when the ledger still holds the exact record the caller captured
 * before queueing. A chained finalization runs after an earlier one settles,
 * and that earlier run may have finalized (cleared) or re-parked the round;
 * acting on the stale capture would re-run an already-settled round. */
function isLedgerRecordCurrent(
  meetingId: string,
  capturedRecord: MeetingPostProcessingJobState
): boolean {
  return meetingStore.getPostProcessingJob(meetingId) === capturedRecord;
}

function logFinalizationTelemetry(
  meetingId: string,
  kind: MeetingFinalizationKind,
  fields: { replaceFromOffset?: number | null; replacedCharCount?: number } = {}
) {
  logger.info('meeting post-processing finalization telemetry', {
    category: 'meeting_notes',
    log: {
      metadata: {
        event_type: 'meeting_post_processing_finalization',
        meeting_id: meetingId,
        finalization_kind: kind,
        replace_from_offset: fields.replaceFromOffset ?? null,
        replaced_char_count: fields.replacedCharCount ?? null,
      },
    },
  });
  ppDebug('finalization', {
    meetingId,
    kind,
    replaceFromOffset: fields.replaceFromOffset ?? null,
    replacedCharCount: fields.replacedCharCount ?? null,
  });
}

/** Wait for any in-flight summary workflow to finish before issuing a new
 * summarize; a summarize while one runs is silently dropped (design §3.3). */
async function summarizeAfterSettling(meetingId: string) {
  const meeting = getStores().meetings.get(meetingId);
  if (meeting) {
    try {
      await when(() => meeting.summaryStatus !== 'in_progress', {
        timeout: SUMMARY_SETTLE_TIMEOUT_MS,
      });
    } catch {
      // Timed out waiting; proceed anyway — the worst case is a dropped
      // summarize that a manual retry can re-issue.
    }
  }
  await getStores().meetings.summarizeMeeting(meetingId, { automatic: true });
}

/** Completes the summary step after the transcript is final. The stop path
 * already decided whether this round should defer its first summary. */
async function completeSummaryStep(
  meetingId: string,
  ledger: RoundLedger
): Promise<boolean> {
  if (ledger.deferSummary) return false;
  await summarizeAfterSettling(meetingId);
  return true;
}

function setSummaryFinalizingFromLedger(meetingId: string): boolean {
  const record = meetingStore.getPostProcessingJob(meetingId);
  const summaryStillFinalizing =
    record?.state === 'running' &&
    !record.releasePending &&
    !record.liveSummarizedAt &&
    !record.summaryDeferredReadyAt;
  meetingStore.setSummaryFinalizing(meetingId, summaryStillFinalizing);
  return summaryStillFinalizing;
}

function finishSummaryFinalization(
  meetingId: string,
  summaryGenerated: boolean
) {
  if (!setSummaryFinalizingFromLedger(meetingId)) {
    onPostProcessingFinalized?.(meetingId, summaryGenerated);
  }
}

/** Non-destructively keep the already-streamed live transcript as the final
 * one: ingest the remnant, summarize, ack (audio deleted), clear the record.
 * On failure the record is left intact and un-acked so reconcile retries.
 * `boundaryUnknown` forces the persisted boundary to null even with a known
 * watermark, for rounds where the ledger's length view is proven stale. */
async function finalizeKeepLive(
  meetingId: string,
  ledger: RoundLedger,
  options: { boundaryUnknown?: boolean } = {}
): Promise<boolean> {
  let summaryGenerated: boolean;
  try {
    await backendApis.meetingNotes.ingestRemainingTranscript(meetingId);
    summaryGenerated = await completeSummaryStep(meetingId, ledger);
  } catch (error) {
    console.warn(
      '[meetings] Keep-live finalization failed, will retry:',
      error
    );
    return false;
  }
  // The kept live transcript is settled up to the post-flush length captured
  // at stop. A null-watermark round (background promotion) never learned it,
  // and a replace 409 (boundaryUnknown) proves the captured length is stale;
  // both must persist "unknown" rather than a guessed offset so a later
  // crash-resume keeps live instead of replacing from a wrong boundary.
  await completePostProcessingRound(meetingId, ledger, {
    boundary:
      !options.boundaryUnknown && hasReplaceWatermark(ledger)
        ? ledger.expectedLength
        : null,
    kind: 'kept_live',
    summaryGenerated,
  });
  return true;
}

async function completePostProcessingRound(
  meetingId: string,
  ledger: RoundLedger,
  finalized: FinalizedPostProcessingRound
) {
  if (!canWriteLedger(meetingId)) return;
  if (!completeTranscriptProcessingRound(meetingId, ledger, finalized)) return;
  await releaseTranscriptProcessing(meetingId);
}

/** Discard the round without acking (audio retained) and keep the ledger
 * record so the next stop re-runs post-processing over the retained sessions. */
function discardRound(meetingId: string) {
  logFinalizationTelemetry(meetingId, 'discarded_stale');
}

/** Native reported a retryable failure with no transcript to replace. Give the
 * user a summary from the intact live transcript, but keep the round un-acked
 * (audio survives for reprocessing) and the running record intact. A marker on
 * the record makes a re-delivered failure idempotent so reconcile does not
 * re-summarize in a loop. */
async function summarizeRetryableFailureLive(
  meetingId: string,
  ledger: RoundLedger
) {
  const record = meetingStore.getPostProcessingJob(meetingId);
  if (
    record?.state === 'running' &&
    (record.liveSummarizedAt || record.summaryDeferredReadyAt)
  ) {
    // This round's live transcript is already summary-ready; nothing more to do
    // (no ack, record kept — the round still reprocesses at the next stop).
    return;
  }
  let summaryGenerated: boolean;
  try {
    await backendApis.meetingNotes.ingestRemainingTranscript(meetingId);
    summaryGenerated = await completeSummaryStep(meetingId, ledger);
  } catch (error) {
    console.warn(
      '[meetings] Retryable-failure live summarize failed, will retry:',
      error
    );
    return;
  }
  // After a mid-finalization user switch, do not stamp the old user's record.
  if (!canWriteLedger(meetingId)) return;
  if (summaryGenerated) {
    meetingStore.markPostProcessingJobLiveSummarized(meetingId);
  } else {
    meetingStore.markPostProcessingJobSummaryDeferredReady(meetingId);
  }
  finishSummaryFinalization(meetingId, summaryGenerated);
  logFinalizationTelemetry(meetingId, 'failed_retryable_kept_live');
}

/** True when the ledger has a usable code-point watermark and expected length.
 * A null/absent watermark means "unknown" → we must keep-live, never replace
 * with a guessed offset (design §4.4 "ledger lost"). */
function hasReplaceWatermark(ledger: RoundLedger): ledger is RoundLedger & {
  replaceFromOffset: number;
  expectedLength: number;
} {
  return (
    typeof ledger.replaceFromOffset === 'number' &&
    typeof ledger.expectedLength === 'number'
  );
}

/**
 * A replace only lands on the backend; the renderer still shows the pre-replace
 * transcript — the in-session live copy cached in the store, or the value a
 * bootstrap synced before the replace. Pull the assembled post-processed
 * transcript and push it into the committed-transcript cache so the UI reflects
 * it without an app restart. Best-effort: on a fetch failure the intact live
 * transcript stays on screen until the next sync, which beats blanking it.
 */
async function refreshReplacedTranscriptForDisplay(meetingId: string) {
  try {
    const transcriptState =
      await getStores().meetings.fetchMeetingTranscript(meetingId);
    if (transcriptState.personifiedTranscript !== null) {
      meetingStore.setCommittedTranscript(
        meetingId,
        transcriptState.personifiedTranscript
      );
    }
  } catch (error) {
    console.warn(
      '[meetings] Failed to refresh displayed transcript after replace:',
      error
    );
  }
}

/**
 * Core replace finalization: replace the live suffix with `segments`, re-ingest,
 * summarize (after the in-flight summary settles), ack, and clear the record.
 *
 * Returns the outcome so callers (auto-run vs manual retry) can react:
 * - `replaced`: success (record cleared, audio acked)
 * - `failed`: retryable failure (caller decides park vs retry)
 * - `kept_live`: watermark unknown, empty result, or 409 → kept the live
 *   transcript instead (record cleared, audio acked)
 */
async function runReplaceFinalization(
  meetingId: string,
  ledger: RoundLedger,
  segments: LocalTranscriptSegment[]
): Promise<'replaced' | 'failed' | 'kept_live'> {
  if (segments.length === 0 || !hasReplaceWatermark(ledger)) {
    // Empty result or unknown watermark: never wipe live content.
    return (await finalizeKeepLive(meetingId, ledger)) ? 'kept_live' : 'failed';
  }

  const { replaceFromOffset, expectedLength } = ledger;
  try {
    const result = await replaceTranscriptSuffix(
      {
        replaceTranscriptSuffix:
          backendApis.meetingNotes.replaceTranscriptSuffix,
      },
      {
        meetingId,
        startOffset: replaceFromOffset,
        expectedLength,
        segments,
        deviceId: await electronApis.system.getDeviceId(),
        processingToken: ledger.processingToken,
      }
    );
    await backendApis.meetingNotes.ingestRemainingTranscript(meetingId);
    const summaryGenerated = await completeSummaryStep(meetingId, ledger);
    await completePostProcessingRound(meetingId, ledger, {
      boundary: result.currentLength,
      kind: 'replaced',
      summaryGenerated,
    });
    return 'replaced';
  } catch (error) {
    if (error instanceof TeamPolicyRejectedError) {
      await completePostProcessingRound(meetingId, ledger, {
        boundary: null,
        kind: 'kept_live',
        summaryGenerated: false,
      });
      return 'kept_live';
    }
    if (error instanceof TranscriptChangedError) {
      // 409: another writer changed the transcript, so the retained segments
      // are no longer a valid suffix. A pure discard would strand the round
      // forever: native re-delivers the un-acked terminal on every reconcile,
      // each replay 409s again, and no summary is ever generated. Degrade to
      // keep-live instead (as the resume path does). The 409 also proves the
      // ledger's length view is stale (a foreign writer appended), so the
      // persisted boundary must become unknown, never ledger.expectedLength.
      const keptLive = await finalizeKeepLive(meetingId, ledger, {
        boundaryUnknown: true,
      });
      return keptLive ? 'kept_live' : 'failed';
    }
    if (error instanceof ReplaceTranscriptSuffixFailedError) {
      return 'failed';
    }
    // A non-replace failure (ingest/summary/ack). The replace already landed
    // idempotently, so treat as retryable: the record is parked and a retry
    // re-runs the idempotent replace.
    console.warn('[meetings] Post-replace finalization step failed:', error);
    return 'failed';
  }
}

/** Parks a completed round after a retryable replace failure: retain the
 * segments + watermark, summarize the live transcript so the user has one,
 * and leave the audio un-acked. A parked round holds no retry budget: it
 * waits for a manual retry or the next resume. */
async function parkRound(
  meetingId: string,
  ledger: RoundLedger,
  completed: MeetingPostProcessingParkedState['completed']
) {
  // After a mid-finalization user switch, parking would stamp the old user's
  // round with the new user's id; leave the ledger alone (the owner's next
  // session reconciles the still-running record).
  if (!canWriteLedger(meetingId)) return;
  const parkedState: MeetingPostProcessingParkedState = {
    state: 'failed',
    meetingId,
    completed,
    replaceFromOffset: ledger.replaceFromOffset,
    sessionEpoch: ledger.sessionEpoch,
    processingToken: ledger.processingToken,
    expectedLength: ledger.expectedLength,
    deferSummary: ledger.deferSummary,
    failed: {
      errorCode: 'backend_upload_failed',
      message:
        'Transcript upload failed. The live transcript is still available; retry to finish.',
    },
  };
  meetingStore.setDurablePostProcessingJobState(parkedState);
  ppDebug('round_parked', {
    meetingId,
    sessionEpoch: ledger.sessionEpoch ?? null,
  });
  // Give system-stopped meetings a usable summary while parked. Explicitly
  // stopped meetings expose the manual summary action once sync completes.
  let summaryGenerated: boolean;
  try {
    summaryGenerated = await completeSummaryStep(meetingId, ledger);
  } catch (error) {
    console.warn(
      '[meetings] Failed to summarize live transcript while parked:',
      error
    );
    return;
  }
  if (!summaryGenerated) {
    meetingStore.markPostProcessingJobSummaryDeferredReady(meetingId);
  }
  finishSummaryFinalization(meetingId, summaryGenerated);
  logFinalizationTelemetry(meetingId, 'parked', {
    replaceFromOffset: ledger.replaceFromOffset,
  });
}

/** Runs the replace budget: retry the replace up to the cap, then park. */
async function finalizeCompletedRound(
  meetingId: string,
  ledger: RoundLedger,
  segments: LocalTranscriptSegment[]
) {
  for (
    let attemptCount = 0;
    attemptCount < MAX_BACKEND_UPLOAD_FINALIZATION_ATTEMPTS;
    attemptCount += 1
  ) {
    const outcome = await runReplaceFinalization(meetingId, ledger, segments);
    if (outcome !== 'failed') return;
  }
  await parkRound(meetingId, ledger, { transcript: { segments } });
}

function isStaleResult(state: TerminalStatePayload): boolean {
  if (meetingStore.isMeetingIdRecording(state.meetingId)) return true;

  const record = meetingStore.getPostProcessingJob(state.meetingId);
  const recordEpoch = ledgerFromRecord(record).sessionEpoch;
  // A missing wire epoch on a terminal payload is treated as non-matching
  // (stale): we cannot prove the result belongs to the current round.
  return state.sessionEpoch === undefined || state.sessionEpoch !== recordEpoch;
}

/**
 * A stale terminal means the native job for that (older) round just ended, so
 * the coordinator's `start()` — which no-ops while a job is active — will now
 * run. If the ledger already advanced to a NEWER-epoch running round (a
 * resume+stop queued the next round but its `start()` was dropped), re-issue it
 * or the meeting never finalizes (design I3). Only fires when NOT recording and
 * strictly newer than the discarded terminal, so it cannot loop: the re-issued
 * round's own terminal carries the matching epoch and finalizes normally.
 */
async function maybeReissueStrandedRound(
  meetingId: string,
  discardedEpoch: number | undefined
) {
  if (meetingStore.isMeetingIdRecording(meetingId)) return;

  // A missing discarded epoch cannot be proven older than the record, so never
  // re-issue on it — only on a terminal strictly older than a newer round.
  if (discardedEpoch === undefined) return;

  const record = meetingStore.getPostProcessingJob(meetingId);
  if (record?.state !== 'running') return;
  const recordEpoch = record.sessionEpoch;
  if (recordEpoch === undefined || recordEpoch <= discardedEpoch) return;

  try {
    await startLocalPostProcessing(meetingId, {
      replaceFromOffset: record.replaceFromOffset,
      sessionEpoch: recordEpoch,
      processingToken: record.processingToken,
      expectedLength: record.expectedLength ?? 0,
      deferSummary: record.deferSummary ?? false,
    });
  } catch (error) {
    // Reconcile retries later; do not throw out of terminal handling.
    console.warn('[meetings] Failed to re-issue stranded round:', error);
  }
}

async function handleTerminalState(state: TerminalStatePayload) {
  const meetingId = state.meetingId;
  const record = meetingStore.getPostProcessingJob(meetingId);
  if (record?.state === 'running' && record.finalized) {
    await releaseTranscriptProcessing(meetingId);
    return;
  }
  const stale = isStaleResult(state);
  ppDebug('terminal_received', {
    meetingId,
    state: state.state,
    sessionEpoch: state.sessionEpoch ?? null,
    stale,
  });

  if (stale) {
    // Discard: no replace, no ack (audio retained), keep the record + watermark.
    discardRound(meetingId);
    await maybeReissueStrandedRound(meetingId, state.sessionEpoch);
    return;
  }

  const ledger = ledgerFromRecord(meetingStore.getPostProcessingJob(meetingId));

  if (state.state === 'completed') {
    await finalizeCompletedRound(
      meetingId,
      ledger,
      splitTranscriptSegments(state.completed.transcript.segments)
    );
    return;
  }

  // Native terminal failure. Retryability comes from the wire, not a code table.
  if (state.failed.retryable) {
    await summarizeRetryableFailureLive(meetingId, ledger);
    return;
  }
  // Terminal, non-retryable / empty / quality-gated: keep the live transcript.
  await finalizeKeepLive(meetingId, ledger);
}

function ensurePostProcessingStateListener() {
  if (unlistenPostProcessingState) return;

  unlistenPostProcessingState =
    electronApis.meetings.listenPostProcessingStateChanged(
      (state) => {
        const meetingId = state.meetingId.toLowerCase();
        meetingDebugStore.record({
          kind: 'post_processing_state',
          meetingId,
          summary: `Post-processing ${state.state}`,
          fields: {
            state: state.state,
            sessionEpoch: state.sessionEpoch,
            failed: state.state === 'failed' ? state.failed : undefined,
          },
        });
        const handler = postProcessingStateHandlers.get(meetingId);
        if (!handler) return;
        return handler({ ...state, meetingId });
      },
      () => {
        // A payload that fails validation makes ElectronAPI remove the
        // underlying listener AFTER this callback returns. Re-attach on the
        // microtask queue so one malformed event cannot silently sever every
        // later terminal event (whose payload carries the only transcript copy).
        unlistenPostProcessingState = undefined;
        queueMicrotask(() => {
          if (postProcessingStateHandlers.size > 0) {
            ensurePostProcessingStateListener();
          }
        });
      }
    );
}

function releasePostProcessingStateHandler(meetingId: string) {
  postProcessingStateHandlers.delete(meetingId);
  syncPostProcessingActivity();
  if (postProcessingStateHandlers.size > 0) return;

  unlistenPostProcessingState?.();
  unlistenPostProcessingState = undefined;
}

function registerPostProcessingStateHandler(meetingId: string) {
  const handlePostProcessingState = async (
    state: MeetingPostProcessingStateChangedPayload
  ) => {
    releasePostProcessingStateHandler(meetingId);
    await runPostProcessingFinalization(meetingId, () =>
      handleTerminalState(state)
    );
  };

  ensurePostProcessingStateListener();
  postProcessingStateHandlers.set(meetingId, handlePostProcessingState);
  syncPostProcessingActivity();
}

async function releaseTranscriptProcessing(meetingId: string) {
  const record = await releaseTranscriptProcessingRound(meetingId);
  if (!record?.finalized) return;
  if (record.finalized.kind === 'replaced') {
    await refreshReplacedTranscriptForDisplay(meetingId);
  }
  finishSummaryFinalization(meetingId, record.finalized.summaryGenerated);
  logFinalizationTelemetry(meetingId, record.finalized.kind, {
    replaceFromOffset:
      record.finalized.kind === 'replaced'
        ? record.replaceFromOffset
        : undefined,
    replacedCharCount:
      record.finalized.kind === 'replaced' && record.finalized.boundary !== null
        ? record.finalized.boundary - (record.replaceFromOffset ?? 0)
        : undefined,
  });
}

/** Asks native where a hydrated `running` job stands: still running (wait for
 * the event), finished while no renderer listened (handle the retained
 * payload), or unknown (observer restarted; artifact recovery covers it). */
async function syncRunningPostProcessingJob(meetingId: string) {
  let jobState;
  try {
    jobState = await electronApis.meetings.getPostProcessingJobState({
      meetingId,
    });
  } catch (error) {
    console.warn(
      '[meetings] Failed to sync running post-processing job:',
      error
    );
    return;
  }

  switch (jobState.status) {
    case 'running':
      return;
    case 'completed':
      await postProcessingStateHandlers.get(meetingId)?.({
        state: 'completed',
        meetingId,
        sessionEpoch: jobState.sessionEpoch,
        completed: jobState.completed,
      });
      return;
    case 'failed':
      await postProcessingStateHandlers.get(meetingId)?.({
        state: 'failed',
        meetingId,
        sessionEpoch: jobState.sessionEpoch,
        failed: jobState.failed,
      });
      return;
    case 'unknown': {
      // Inbound ContextKit messages are not processed strictly in order: a
      // sync-state racing a just-sent start can reach the coordinator first
      // and report 'unknown' for a job that is about to register. Only drop
      // entries old enough that 'unknown' really means the observer lost them.
      const storedState = meetingStore.getPostProcessingJob(meetingId);
      if (
        storedState?.state === 'running' &&
        Date.now() - new Date(storedState.startedAt).getTime() <
          POST_PROCESSING_SYNC_UNKNOWN_GRACE_MS
      ) {
        return;
      }
      releasePostProcessingStateHandler(meetingId);
      await releaseTranscriptProcessing(meetingId);
      return;
    }
  }
}

export async function startLocalPostProcessing(
  meetingId: string,
  ledger: RunningPostProcessingLedgerFields,
  session?: RecordingSession
) {
  const previous = meetingStore.getPostProcessingJob(meetingId);
  if (
    previous?.state === 'running' &&
    previous.processingToken !== ledger.processingToken
  ) {
    await releaseTranscriptProcessing(meetingId);
    if (meetingStore.getPostProcessingJob(meetingId)) {
      throw new Error('Previous transcript processing is still being released');
    }
  }
  const meetingVocabulary =
    getStores().meetings.get(meetingId)?.customVocabulary;
  // Native echoes sessionEpoch verbatim on the terminal event so a stale
  // round's result can be discarded against the ledger's epoch.
  const startPayload = {
    meetingId: meetingId.toLowerCase(),
    sessionEpoch: ledger.sessionEpoch,
    customVocabulary: meetingVocabulary?.length
      ? toJS(meetingVocabulary)
      : undefined,
  };

  // Persist the job before asking native to start: if this renderer session
  // dies while the job runs, reconcile finds the running entry and re-attaches
  // a handler instead of dropping the eventual terminal event on the floor.
  meetingStore.setPostProcessingJobRunning(startPayload.meetingId, ledger);
  meetingStore.setSummaryFinalizing(startPayload.meetingId, true);
  ppDebug('round_issued', {
    meetingId: startPayload.meetingId,
    sessionEpoch: ledger.sessionEpoch,
    replaceFromOffset: ledger.replaceFromOffset,
    expectedLength: ledger.expectedLength,
  });

  try {
    await beginTranscriptProcessingRound(
      startPayload.meetingId,
      ledger,
      session
    );
    await backendApis.meetingNotes.ingestRemainingTranscript(
      startPayload.meetingId
    );
    registerPostProcessingStateHandler(startPayload.meetingId);
    await electronApis.meetings.startPostProcessing(startPayload);
  } catch (error) {
    releasePostProcessingStateHandler(startPayload.meetingId);
    // Only remove the entry this start created; never a durable
    // failed entry that predates it.
    const storedState = meetingStore.getPostProcessingJob(
      startPayload.meetingId
    );
    if (
      storedState?.state === 'running' &&
      storedState.processingToken === ledger.processingToken
    ) {
      await releaseTranscriptProcessing(startPayload.meetingId);
    }
    meetingStore.setSummaryFinalizing(startPayload.meetingId, false);
    console.warn('[meetings] Failed to start post-processing:', error);
    throw error;
  }
}

export async function reconcilePostProcessingJobs() {
  if (!isCapable(AppCapability.DesktopMainWindowExclusive)) return;

  for (const storedState of meetingStore.postProcessingJobs.values()) {
    // Jobs belong to the user who recorded them; never retry another
    // account's finalization (an in-flight failure can re-insert an entry
    // after the logout clear, so hydrate-filtering alone is not enough).
    if (storedState.userId !== userStore.userId) continue;
    setSummaryFinalizingFromLedger(storedState.meetingId);
    if (storedState.state === 'running') {
      if (storedState.releasePending) {
        await runPostProcessingFinalization(storedState.meetingId, async () => {
          if (isLedgerRecordCurrent(storedState.meetingId, storedState)) {
            await releaseTranscriptProcessing(storedState.meetingId);
          }
        });
        continue;
      }
      // The job's terminal event may still arrive, or arrived while no
      // renderer was listening; attach a handler, then ask native which.
      registerPostProcessingStateHandler(storedState.meetingId);
      await syncRunningPostProcessingJob(storedState.meetingId);
    }
    // Parked rounds are deliberately not re-run here: parked = waits for a
    // manual retry or the next resume (resolveParkedRoundBeforeResume).
  }
}

/** True when a completed transcript's replace gave up after the auto-retry
 * cap; the segments are retained locally awaiting a manual retry. */
export function hasParkedPostProcessingUpload(meetingId: string): boolean {
  return isParkedRound(meetingStore.getPostProcessingJob(meetingId));
}

/** Manual retry for a parked round: re-attempts the same idempotent replace
 * with a fresh replace budget (a user click is a fresh decision). */
export async function retryPostProcessingFinalization(meetingId: string) {
  const normalizedMeetingId = meetingId.toLowerCase();
  const storedState = meetingStore.getPostProcessingJob(normalizedMeetingId);
  if (!isParkedRound(storedState)) return;

  await runPostProcessingFinalization(normalizedMeetingId, async () => {
    if (!isLedgerRecordCurrent(normalizedMeetingId, storedState)) return;
    await finalizeCompletedRound(
      normalizedMeetingId,
      ledgerFromRecord(storedState),
      storedState.completed.transcript.segments
    );
  });
}

/**
 * Resume-while-parked (design §4.4): before a new session starts streaming,
 * attempt the parked round's replace synchronously — the ledger payload is
 * still a valid suffix at this moment. On failure, degrade round k to keep-live
 * (ack + clear) so round k+1 can start from a fresh watermark.
 *
 * Completes a persisted release/acknowledgement before allowing a new recording.
 * Returns true if a pending round was handled (replaced or degraded), so the
 * caller knows the watermark for the next round must be re-captured fresh.
 */
export async function resolveParkedRoundBeforeResume(
  meetingId: string
): Promise<boolean> {
  const normalizedMeetingId = meetingId.toLowerCase();
  await postProcessingUploadsInFlight.get(normalizedMeetingId)?.catch(() => {});
  const storedState = meetingStore.getPostProcessingJob(normalizedMeetingId);
  if (
    !isParkedRound(storedState) &&
    !(storedState?.state === 'running' && storedState.finalized)
  )
    return false;

  await runPostProcessingFinalization(normalizedMeetingId, async () => {
    if (!isLedgerRecordCurrent(normalizedMeetingId, storedState)) return;
    if (storedState.state === 'running') {
      await releaseTranscriptProcessing(normalizedMeetingId);
      return;
    }
    const outcome = await runReplaceFinalization(
      normalizedMeetingId,
      ledgerFromRecord(storedState),
      storedState.completed.transcript.segments
    );
    if (outcome === 'replaced' || outcome === 'kept_live') return;
    // Retryable failure at resume time: degrade round k to keep-live so
    // the new session gets a clean watermark. The live transcript is intact.
    // If the degrade itself fails (ingest/summarize threw), the record stays
    // parked and reconcile retries later; surface it so the drop is not silent.
    const degraded = await finalizeKeepLive(
      normalizedMeetingId,
      ledgerFromRecord(storedState)
    );
    if (!degraded) {
      console.warn(
        '[meetings] Parked-round degrade-to-keep-live failed on resume; record stays parked for retry:',
        normalizedMeetingId
      );
    }
  });
  const pending = meetingStore.getPostProcessingJob(normalizedMeetingId);
  if (pending?.state === 'running' && pending.finalized) {
    throw new Error(
      'Transcript processing is still finishing. Try again shortly.'
    );
  }
  return true;
}

export async function waitForPostProcessingFinalization() {
  const drain = (async () => {
    await reconcilePostProcessingJobs();
    await Promise.all(Array.from(postProcessingUploadsInFlight.values()));
  })().catch((error) => {
    console.warn('[meetings] Failed to drain post-processing jobs:', error);
  });
  let warnTimer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<void>((resolve) => {
    warnTimer = setTimeout(() => {
      console.warn(
        `[meetings] Post-processing finalization drain timed out after ${POST_PROCESSING_FINALIZATION_DRAIN_TIMEOUT_MS}ms`
      );
      resolve();
    }, POST_PROCESSING_FINALIZATION_DRAIN_TIMEOUT_MS);
  });
  await Promise.race([drain, timeout]);
  clearTimeout(warnTimer);
}
